import { TextField as RadixTextField } from '@radix-ui/themes'
import { ReactNode, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'

interface TextFieldProps {
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  type?:
    | 'text'
    | 'password'
    | 'email'
    | 'number'
    | 'tel'
    | 'url'
    | 'search'
    | 'date'
    | 'time'
    | 'datetime-local'
    | 'month'
    | 'week'
    | 'hidden'
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  className?: string
  style?: React.CSSProperties
  size?: '1' | '2' | '3'
  radius?: 'none' | 'small' | 'medium' | 'large' | 'full'
  variant?: 'classic' | 'surface' | 'soft'
  disabled?: boolean
  required?: boolean
  name?: string
  showPasswordToggle?: boolean
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void
}

const TextField = ({
  value,
  onChange,
  placeholder,
  type = 'text',
  icon,
  iconPosition = 'left',
  className = '',
  style = {},
  size = '3',
  radius = 'large',
  variant = 'soft',
  disabled = false,
  required = false,
  name,
  showPasswordToggle = false,
  onKeyDown,
  onPaste
}: TextFieldProps) => {
  const [showPassword, setShowPassword] = useState(false)

  const defaultStyle = {
    background: 'var(--color-card)',
    height: '3.44rem',
    ...style
  }

  // Determine the actual input type
  const inputType =
    type === 'password' && showPasswordToggle && showPassword ? 'text' : type

  // Determine if we should show the password toggle icon
  const shouldShowToggle = type === 'password' && showPasswordToggle

  return (
    <div className="w-full">
      <div className="relative">
        <RadixTextField.Root
          size={size}
          radius={radius}
          type={inputType}
          variant={variant}
          style={defaultStyle}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full text-sm lg:text-base shadow-sm border border-border-base dark:border-0 ${className}`}
          disabled={disabled}
          required={required}
          name={name}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        >
          {icon && iconPosition === 'left' && (
            <RadixTextField.Slot>{icon}</RadixTextField.Slot>
          )}
          {shouldShowToggle ? (
            <RadixTextField.Slot side="right">
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-text-secondary pr-2 hover:text-text-main transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4 lg:w-5 lg:h-5" />
                ) : (
                  <Eye className="w-4 h-4 lg:w-5 lg:h-5" />
                )}
              </button>
            </RadixTextField.Slot>
          ) : (
            icon &&
            iconPosition === 'right' && (
              <RadixTextField.Slot side="right">{icon}</RadixTextField.Slot>
            )
          )}
        </RadixTextField.Root>
      </div>
    </div>
  )
}

export default TextField
